/**
 * LimitWarning Component - Show warning when user is approaching or at limit
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserEntitlements } from '../../hooks/useUserEntitlements';

const LimitWarning = ({ limitType, showWhenNear = true, showWhenAt = true }) => {
  const { entitlements, loading } = useUserEntitlements();
  const navigate = useNavigate();

  if (loading || !entitlements || entitlements.is_admin) {
    return null;
  }

  const limit = entitlements.limits?.[limitType];
  if (!limit || limit.total === -1) return null;

  const percentage = (limit.current_usage / limit.total) * 100;
  const isNearLimit = percentage >= 75 && percentage < 100;
  const isAtLimit = limit.current_usage >= limit.total;

  if (!isNearLimit && !isAtLimit) return null;
  if (isNearLimit && !showWhenNear) return null;
  if (isAtLimit && !showWhenAt) return null;

  return (
    <div className={`rounded-lg border p-4 mb-4 ${
      isAtLimit 
        ? 'bg-red-50 border-red-200' 
        : 'bg-yellow-50 border-yellow-200'
    }`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {isAtLimit ? (
            <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ) : (
            <svg className="h-5 w-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
        </div>
        
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${
            isAtLimit ? 'text-red-800' : 'text-yellow-800'
          }`}>
            {isAtLimit ? 'Limit Reached' : 'Approaching Limit'}
          </h3>
          
          <div className={`mt-2 text-sm ${
            isAtLimit ? 'text-red-700' : 'text-yellow-700'
          }`}>
            <p>
              {isAtLimit
                ? `You've reached your ${limitType.replace(/_/g, ' ')} limit (${limit.total}). Upgrade to create more.`
                : `You've used ${limit.current_usage} of ${limit.total} ${limitType.replace(/_/g, ' ')} (${Math.round(percentage)}%).`
              }
            </p>
          </div>

          <div className="mt-4 flex space-x-3">
            <button
              onClick={() => navigate('/subscription')}
              className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md ${
                isAtLimit
                  ? 'text-white bg-red-600 hover:bg-red-700'
                  : 'text-yellow-800 bg-yellow-100 hover:bg-yellow-200'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500`}
            >
              {isAtLimit ? 'Upgrade Now' : 'View Upgrade Options'}
            </button>
            
            {!isAtLimit && (
              <button
                onClick={() => {/* Close warning */}}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-transparent hover:bg-yellow-100 focus:outline-none"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LimitWarning;
